import { capitalize } from 'lodash'
import React, { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router'
import styled from 'styled-components'

import { useAnnotationHighlightStore } from 'features/ask-yogi/annotation-highlight-store'
import {
  ResourceCard,
  type ResourceCardIcon,
  type ResourceCardProps,
} from 'features/ask-yogi/features/conversation/components/ResourceCard/ResourceCard'
import { useIsDashboards21Enabled } from 'features/ask-yogi/hooks/use-ask-yogi-feature-flag'
import type { AgentAnnotationArtifact } from 'features/ask-yogi/types'
import {
  buildAnnotationPath,
  isAnnotationForPage,
  isFeatureSupported,
} from 'features/ask-yogi/utils/annotation-routing'
import type { HashNavigationState } from 'routes/navigation-state'

interface AgentResourceCreationAnnotationProps {
  annotation: AgentAnnotationArtifact
  projectId: string
  /** The conversation the card is in: a dashboard link opens with it docked. */
  conversationId?: string
}

const displayLabel = (ann: AgentAnnotationArtifact): string =>
  ann.label ?? `${ann.action} ${ann.resource}`

// The `resource` noun is backend-supplied free text (e.g. "dashboard", or
// "view" for a saved charts view); the routable features are
// `annotation-routing.ts`'s FEATURE_PATH_BUILDERS. Unmapped resource nouns
// fall back to the dashboard icon rather than growing a third ResourceCard
// icon.
const RESOURCE_ICON_BY_NAME: Record<string, ResourceCardIcon> = {
  dashboard: 'dashboard',
  chart: 'chart',
  view: 'chart',
}

// A link opens the resource exactly as saved: without `hashAuthority: 'url'`
// the charts page overlays the user's current filters onto a saved view's
// hash on an in-app (PUSH) navigation. Dashboards ignore the state.
const HASH_AUTHORITATIVE: { state: HashNavigationState } = {
  state: { hashAuthority: 'url' },
}

const resourceIcon = (resource: string): ResourceCardIcon =>
  RESOURCE_ICON_BY_NAME[resource] ?? 'dashboard'

export const AgentResourceCreationAnnotation: React.FC<
  AgentResourceCreationAnnotationProps
> = ({ annotation, projectId, conversationId }) => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const setHighlight = useAnnotationHighlightStore((s) => s.setHighlight)
  const cancelHide = useAnnotationHighlightStore((s) => s.cancelHide)
  const scheduleHide = useAnnotationHighlightStore((s) => s.scheduleHide)
  const clearHighlight = useAnnotationHighlightStore((s) => s.clearHighlight)
  const isDashboards21Enabled = useIsDashboards21Enabled()
  const supported = isFeatureSupported(annotation.feature)
  const path = supported
    ? buildAnnotationPath(projectId, annotation, conversationId)
    : null

  // Unmounting skips the mouseleave, which would leave the nav preview stuck
  // on every page. Only this card's highlight is ours to clear.
  useEffect(
    () => () => {
      const { highlight } = useAnnotationHighlightStore.getState()
      if (highlight?.annotation.id === annotation.id) clearHighlight()
    },
    [annotation.id, clearHighlight],
  )

  // A link to the page the user is already on says nothing new.
  if (path && isAnnotationForPage(path, pathname)) return null

  const openResource = (to: string): void => {
    clearHighlight()
    navigate(to, HASH_AUTHORITATIVE)
  }

  if (!isDashboards21Enabled) {
    if (!supported) {
      return (
        <AnnotationText $interactive={false}>
          {displayLabel(annotation)}
        </AnnotationText>
      )
    }

    return (
      <AnnotationText
        $interactive
        onMouseEnter={() => {
          cancelHide()
          setHighlight({ feature: annotation.feature, annotation })
        }}
        onMouseLeave={scheduleHide}
        onClick={() => {
          if (path) openResource(path)
        }}
        role="link"
        tabIndex={0}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && path) openResource(path)
        }}
      >
        {displayLabel(annotation)}
      </AnnotationText>
    )
  }

  // Non-interactive when the feature isn't supported yet, or the feature
  // is supported but this particular annotation carries no resolvable path
  // — no onNavigate, no hover highlight, no arrow affordance.
  const linkProps: ResourceCardProps = {
    variant: 'link',
    icon: resourceIcon(annotation.resource),
    title: displayLabel(annotation),
    subtitle: capitalize(annotation.resource),
    ...(path ? { onNavigate: () => openResource(path) } : {}),
  }

  return (
    <div
      data-testid="agent-resource-annotation"
      onMouseEnter={
        path
          ? () => {
              cancelHide()
              setHighlight({ feature: annotation.feature, annotation })
            }
          : undefined
      }
      onMouseLeave={path ? scheduleHide : undefined}
    >
      <ResourceCard {...linkProps} />
    </div>
  )
}

const AnnotationText = styled.span<{ $interactive: boolean }>`
  text-decoration: underline;
  cursor: ${({ $interactive }) => ($interactive ? 'pointer' : 'default')};
  color: inherit;
`
